/**
 * StepConnectChannel — Second onboarding step: connect a messaging channel.
 *
 * Channel type selector + config form per type. Calls POST /api/onboarding/wizard/channel.
 */
import { useState, type FormEvent } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { api } from "@/lib/api";

type ChannelType = "slack" | "email" | "sms" | "telegram" | "whatsapp";

interface ChannelConfig {
  label: string;
  fields: { key: string; label: string; placeholder: string; type?: string }[];
}

const channelConfigs: Record<ChannelType, ChannelConfig> = {
  slack: {
    label: "Slack",
    fields: [
      { key: "botToken", label: "Bot Token", placeholder: "xoxb-...", type: "password" },
      { key: "signingSecret", label: "Signing Secret", placeholder: "Slack signing secret", type: "password" },
    ],
  },
  email: {
    label: "Email",
    fields: [
      { key: "address", label: "Email Address", placeholder: "support@example.com" },
      { key: "smtpHost", label: "SMTP Host", placeholder: "smtp.example.com" },
      { key: "smtpPort", label: "SMTP Port", placeholder: "587" },
      { key: "smtpUser", label: "SMTP Username", placeholder: "user@example.com" },
      { key: "smtpPass", label: "SMTP Password", placeholder: "••••••••", type: "password" },
    ],
  },
  sms: {
    label: "SMS (Twilio)",
    fields: [
      { key: "accountSid", label: "Account SID", placeholder: "AC..." },
      { key: "authToken", label: "Auth Token", placeholder: "Twilio auth token", type: "password" },
      { key: "phoneNumber", label: "Phone Number", placeholder: "+1234567890" },
    ],
  },
  telegram: {
    label: "Telegram",
    fields: [
      { key: "botToken", label: "Bot Token", placeholder: "123456:ABC-...", type: "password" },
    ],
  },
  whatsapp: {
    label: "WhatsApp",
    fields: [
      { key: "phoneNumberId", label: "Phone Number ID", placeholder: "WhatsApp phone number ID" },
      { key: "accessToken", label: "Access Token", placeholder: "WhatsApp access token", type: "password" },
      { key: "verifyToken", label: "Verify Token", placeholder: "Webhook verify token" },
    ],
  },
};

interface StepConnectChannelProps {
  orgId: string;
  onNext(): void;
  onSkip(): void;
}

export function StepConnectChannel({ orgId, onNext, onSkip }: StepConnectChannelProps) {
  const [channelType, setChannelType] = useState<ChannelType>("slack");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateField(key: string, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await api.post("/api/onboarding/wizard/channel", {
        orgId,
        channelType,
        config: fields,
      });
      onNext();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to connect channel",
      );
    } finally {
      setIsLoading(false);
    }
  }

  const config = channelConfigs[channelType];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Connect a Channel
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your first messaging channel to start routing conversations.
        </p>
      </div>

      <Select
        label="Channel Type"
        value={channelType}
        onChange={(e) => {
          setChannelType(e.target.value as ChannelType);
          setFields({});
          setError(null);
        }}
      >
        {Object.entries(channelConfigs).map(([key, cfg]) => (
          <option key={key} value={key}>
            {cfg.label}
          </option>
        ))}
      </Select>

      {config.fields.map((field) => (
        <Input
          key={field.key}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type ?? "text"}
          value={fields[field.key] ?? ""}
          onChange={(e) => updateField(field.key, e.target.value)}
          required
        />
      ))}

      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-3">
        <Button type="submit" className="flex-1" loading={isLoading}>
          Connect Channel
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
        >
          Skip for now
        </Button>
      </div>
    </form>
  );
}
